import ProductCardSkeleton from './ProductCardSkeleton';

interface CategorySkeletonProps {
  className?: string;
  loading?: boolean;
}

const Pill = ({ className = '' }: { className?: string }) => (
  <div className={`rounded-[1.6rem] bg-brand-light-gray ${className}`} />
);

/**
 * Shown during client-side navigation to /c/*. Mirrors CategoryPageHeader +
 * PageMainContent so the swap to real content doesn't shift the layout — keep
 * the paddings, rows and grid columns here in sync with those components.
 */
const FilterSkeleton = () => (
  <div className="flex w-full flex-col">
    {/* Controls row: breadcrumb (left) + sort/limit/view (right) */}
    <div className="hidden w-full justify-between gap-[1rem] sm:flex">
      <Pill className="h-[3.2rem] w-[20rem]" />
      <div className="flex gap-[1.6rem]">
        <Pill className="h-[3.2rem] w-[10rem]" />
        <Pill className="h-[3.2rem] w-[7rem]" />
        <Pill className="h-[3.2rem] w-[5rem]" />
      </div>
    </div>

    {/* Filter chips row — matches ListingFilters: 115px button + 200px dropdowns */}
    <div className="w-full py-[0rem] lg:py-[1.4rem]">
      <div className="flex w-full flex-wrap justify-center gap-[1rem] sm:justify-start">
        <div className="h-[42.5px] w-[115px] rounded-[10px] bg-brand-light-gray" />
        <div className="h-[42.5px] w-full flex-grow rounded-[10px] bg-brand-light-gray sm:w-[75%] sm:flex-grow-0 md:max-w-[200px]" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="hidden h-[42.5px] w-[200px] rounded-[10px] bg-brand-light-gray md:block"
          />
        ))}
      </div>
    </div>
  </div>
);

const CategorySkeleton = ({}: CategorySkeletonProps) => {
  return (
    <div className="w-full">
      {/* Page header — same paddings as CategoryPageHeader so the height matches */}
      <div className="relative w-full overflow-hidden bg-[#080C0F]">
        <div
          className="
            mx-auto w-full max-w-default
            px-[2rem] md:px-[4rem] xl:px-default-padding-x
            pt-[2rem] pb-[14rem] sm:pt-[3rem] sm:pb-[19rem]
          "
        >
          {/* Stands in for the h1 (text-[2rem] sm:text-[3rem]) */}
          <div className="h-[2.4rem] w-[24rem] rounded-[1.6rem] bg-white/10 sm:h-[3.6rem] sm:w-[32rem]" />
        </div>
      </div>

      {/* Main content — same container as CategoryOrSubcategoryPage */}
      <div
        className="
          w-full max-w-default pt-[2rem]
          px-[2rem] md:px-[4rem] xl:px-default-padding-x
        "
      >
        <div className="flex w-full flex-col">
          <FilterSkeleton />

          {/* Product grid — same columns/gap as PageMainContent's real grid */}
          <div className="flex h-min w-full flex-col gap-[2rem] py-[0rem] sm:py-[2rem]">
            <div className="grid grid-cols-2 gap-[2rem] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {Array.from({ length: 15 }).map((_, index) => (
                <ProductCardSkeleton key={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategorySkeleton;
